import { MessagesSquare } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { setDock, useChatCounts, useDock } from '../../lib/chat';
import { useSession } from '../../lib/session';
import { ChatView } from './ChatView';
import { InboxPanel } from './InboxPanel';
import { NewChat } from './NewChat';

/** Chat is for adults until the legal review for minors (M46). */
export function useCanChat() {
  const { me } = useSession();
  return Boolean(me && me.ageBand === 'adult' && me.profileComplete);
}

function Count({ n }: { n: number | null | undefined }) {
  if (!n) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute top-[3px] right-px box-border grid h-5 min-w-5 place-items-center rounded-full border-2 border-card bg-brand px-[5px] text-[11px] leading-none font-bold text-ink"
    >
      {n > 9 ? '9+' : n}
    </span>
  );
}

const label = (n: number | null | undefined) => (n ? `Messages, ${n} unread` : 'Messages');

/** Computer: the messages icon beside the bell; opens and closes the chat window. */
export function MessagesButton() {
  const counts = useChatCounts(true);
  const dock = useDock();
  const { pathname } = useLocation();
  const onPage = pathname.startsWith('/messages');
  const n = counts?.unread;
  if (onPage) {
    return (
      <Link
        to="/messages"
        aria-current="page"
        aria-label={label(n)}
        className="relative flex size-11 items-center justify-center rounded-full bg-cream text-ink"
      >
        <MessagesSquare size={22} strokeWidth={1.9} aria-hidden="true" />
        <Count n={n} />
      </Link>
    );
  }
  return (
    <button
      type="button"
      aria-label={label(n)}
      aria-expanded={dock.open}
      onClick={() => setDock({ ...dock, open: !dock.open })}
      className={`relative flex size-11 items-center justify-center rounded-full text-ink ${dock.open ? 'bg-cream' : 'hover:bg-field'}`}
    >
      <MessagesSquare size={22} strokeWidth={1.9} aria-hidden="true" />
      <Count n={n} />
    </button>
  );
}

/** Phone: the messages icon opens Messages as its own page. */
export function MessagesLink() {
  const counts = useChatCounts(true);
  return (
    <Link
      to="/messages"
      aria-label={label(counts?.unread)}
      className="relative flex size-11 shrink-0 items-center justify-center rounded-full text-ink"
    >
      <MessagesSquare size={22} strokeWidth={1.9} aria-hidden="true" />
      <Count n={counts?.unread} />
    </Link>
  );
}

/**
 * The chat window (M38): bottom right on a computer, over whatever page is open, with the inbox
 * or one chat. It stays open while the player moves between pages; Messages has the full page.
 */
export function ChatDock() {
  const dock = useDock();
  const { pathname } = useLocation();
  const [tab, setTab] = useState<'chats' | 'requests'>('chats');
  const [starting, setStarting] = useState(false);
  const close = useCallback(() => setDock({ open: false, conversationId: null }), []);
  const back = useCallback(() => setDock({ open: true, conversationId: null }), []);
  if (!dock.open || pathname.startsWith('/messages')) return null;

  return (
    <div className="fixed right-6 bottom-0 z-40 hidden h-[min(620px,calc(100dvh-88px))] w-[400px] flex-col overflow-hidden rounded-t-[20px] border border-b-0 border-line bg-card shadow-[0_18px_50px_rgba(27,21,47,0.2)] lg:flex">
      {starting ? (
        <NewChat
          onBack={() => setStarting(false)}
          onStarted={(id) => {
            setStarting(false);
            setDock({ open: true, conversationId: id });
          }}
        />
      ) : dock.conversationId ? (
        <ChatView
          key={dock.conversationId}
          id={dock.conversationId}
          variant="dock"
          onBack={back}
          onClose={close}
          onGone={back}
        />
      ) : (
        <InboxPanel
          variant="dock"
          tab={tab}
          onTab={setTab}
          onOpen={(id) => setDock({ open: true, conversationId: id })}
          onNew={() => setStarting(true)}
          onClose={close}
        />
      )}
    </div>
  );
}
